import { useCallback, useContext, useEffect, useState } from "react";
import CustomTable from "../components/CustomTable";
import AddServicePopup from "./components/AddServicePopup";
import { formatCurrency } from "../../../utils/CurrencyFormat";
import SearchAndFilterBar from "../components/SearchAndFilterBar";
import CustomSelect from "../components/CustomSelect";
import { AuthContext } from "../../../contexts/AuthContext";
import { serviceService } from "../../../services/api/serviceService";
import { categoryService } from "../../../services/api/categoryService";
import { firebaseService } from "../../../services/api/firebaseService";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import { StatusBadge } from "../../../constants/statusConstants";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import useDebouncedSearch from "../../../hooks/useDebouncedSearch";

const columns = [
  {
    key: "stt",
    title: "STT",
    render: (v) => <div className="pl-2">{v}</div>,
  },
  {
    key: "madv",
    title: "Mã dịch vụ",
    render: (v) => <span className="text-[#0267FF]">{v}</span>,
  },
  {
    key: "dichvu",
    title: "Tên dịch vụ",
    render: (v) => (
      <div className="flex items-center gap-2">
        <img
          alt="serviceImage"
          src={v.serviceImage || "/images/logo.png"}
          className="w-8 h-8 object-contain rounded"
        />
        <span className="truncate">{v.serviceName}</span>
      </div>
    ),
  },
  {
    key: "loaidichvu",
    title: "Loại dịch vụ",
  },
  {
    key: "uuTien",
    title: "Thứ tự ưu tiên",
    render: (v) => <div className="text-center">{v === 0 ? "Không có" : v}</div>,
  },
  {
    key: "giatien",
    title: "Giá tiền",
    render: (v) => formatCurrency(v),
  },
  {
    key: "trangThai",
    title: "Trạng thái",
    render: (v) => <StatusBadge status={v} />,
  },
];

const ManageService = () => {
  const { token, user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    searchParams.get("categoryId") || "",
  );
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const currentPage = Number(searchParams.get("page")) || 1;
  const fetchCategories = useCallback(
    async (searchValue = "") => {
      if (!token) return;

      try {
        const response = await categoryService.get(searchValue, token);
        setCategoryOptions(
          (response?.categories || []).map((category) => ({
            value: category.id || category._id,
            label: category.name || category.categoryName,
          })),
        );
      } catch (error) {
        console.error("Error fetching service categories:", error);
        toast.error("Không thể tải danh sách loại dịch vụ");
      }
    },
    [token],
  );

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const updateSearchQuery = useCallback(
    (value) => {
      const params = new URLSearchParams(searchParams);
      value ? params.set("search", value) : params.delete("search");
      params.set("page", "1");
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );
  const {
    debouncedSearch: debouncedMainSearch,
    cancelSearch: cancelMainSearch,
  } = useDebouncedSearch(updateSearchQuery, 500);
  const {
    debouncedSearch: debouncedCategorySearch,
    searchImmediately: searchCategoryImmediately,
  } = useDebouncedSearch(fetchCategories, 500);

  useEffect(() => {
    setSelectedCategoryId(searchParams.get("categoryId") || "");
  }, [searchParams]);

  const handleEdit = async (id) => {
    try {
      const res = await serviceService.get_by_id(id, token);
      setSelectedService(res);
      setIsPopupOpen(true);
    } catch (error) {
      toast.error("Không thể tải thông tin chi tiết dịch vụ");
      console.error(error);
    }
  };

  const extractFirebaseImageUrls = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string") return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");
    const firebaseUrls = [];
    images.forEach((img) => {
      const src = img.getAttribute("src");
      if (src && (src.startsWith("https://storage.googleapis.com/") || src.startsWith("https://firebasestorage.googleapis.com/"))) {
        firebaseUrls.push(src);
      }
    });
    return firebaseUrls;
  };

  const fetchListServices = useCallback(async () => {
    try {
      setTableLoading(true);
      const res = await serviceService.get_all(
        searchParams.get("search") || "",
        itemsPerPage,
        currentPage,
        token,
        searchParams.get("categoryId") || "",
      );

      setData(
        (res?.services || []).map((item, i) => ({
          nav_id: item._id,
          stt: (currentPage - 1) * itemsPerPage + i + 1,
          madv: item.serviceCode,
          dichvu: {
            serviceName: item.serviceName,
            serviceImage: item?.image || item.image?.url,
          },
          loaidichvu: item.category?.categoryName || "",
          uuTien: item.priority || 0,
          giatien: item.price,
          trangThai: item.status,
          originalData: item,
        })),
      );
      setTotalPages(res?.totalPages || 1);
    } catch {
      toast.error("Lỗi khi tải danh sách dịch vụ");
    } finally {
      setTableLoading(false);
    }
  }, [token, searchParams, itemsPerPage, currentPage]);

  const handleApplyFilter = () => {
    cancelMainSearch();
    const params = new URLSearchParams(searchParams);
    search ? params.set("search", search) : params.delete("search");
    selectedCategoryId
      ? params.set("categoryId", selectedCategoryId)
      : params.delete("categoryId");
    params.set("page", "1");
    setSearchParams(params);
  };

  useEffect(() => {
    fetchListServices();
  }, [fetchListServices]);

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);

      const itemToDelete = data.find(item => item.nav_id === selectedDeleteId);
      const originalItem = itemToDelete?.originalData;
      const logoUrl = originalItem?.image?.url || originalItem?.image || "";
      const descriptionHtml = itemToDelete?.originalData?.description || "";
      const notesHtml = itemToDelete?.originalData?.notes || "";

      const contentImages = [
        ...extractFirebaseImageUrls(descriptionHtml),
        ...extractFirebaseImageUrls(notesHtml)
      ];

      const allImagesToDelete = [...contentImages];
      if (logoUrl && typeof logoUrl === "string" && logoUrl.startsWith("http")) {
        allImagesToDelete.push(logoUrl);
      }

      await serviceService.delete(selectedDeleteId, token);

      for (const url of allImagesToDelete) {
        if (url.startsWith("https://storage.googleapis.com/") || url.startsWith("https://firebasestorage.googleapis.com/")) {
          try {
            await firebaseService.delete_file(token, url);
          } catch (err) {
            console.warn("Failed to delete image from Firebase:", url, err);
          }
        }
      }

      toast.success("Xóa dịch vụ thành công!");
      fetchListServices();
    } catch (err) {
      toast.error(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Không thể xóa dịch vụ",
      );
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
      setSelectedDeleteId(null);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0 gap-8 relative">
      <div className="shrink-0">
        <SearchAndFilterBar
          searchPlaceholder="Tìm kiếm dịch vụ"
          searchValue={search}
          onSearchClick={handleApplyFilter}
          onSearchChange={(e) => {
            const v = e.target.value;
            setSearch(v);
            debouncedMainSearch(v);
          }}
          filterContent={
            <CustomSelect
              options={categoryOptions}
              value={
                categoryOptions.find(
                  (category) => category.value === selectedCategoryId,
                ) || null
              }
              onChange={(category) =>
                setSelectedCategoryId(category?.value || "")
              }
              placeholder="Tất cả loại dịch vụ"
              width="220px"
              height="42px"
              searchable
              isClearable
              onSearch={debouncedCategorySearch}
              onSearchSubmit={searchCategoryImmediately}
              onDropdownOpen={() => fetchCategories()}
              controlClassName="rounded-lg text-base font-sf-regular"
              dropdownClassName="rounded-lg text-base font-sf-regular"
            />
          }
          filterLabel="Áp dụng"
          onAddClick={() => {
            setSelectedService(null);
            setIsPopupOpen(true);
          }}
          addButtonLabel="Thêm dịch vụ"
        />
      </div>

      <div className="flex-1 min-h-0 overflow-visible">
        <div className="h-full min-h-0 overflow-auto p-[2px]">
          <CustomTable
            data={data}
            columns={columns}
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            setCurrentPage={(p) => {
              const sp = new URLSearchParams(searchParams);
              sp.set("page", p.toString());
              setSearchParams(sp);
            }}
            setEdit={true}
            handleEdit={handleEdit}
            setDelete={user?.role === "SuperAdmin"}
            handleDelete={(id) => {
              setSelectedDeleteId(id);
              setShowDeleteConfirm(true);
            }}
            basePath="/quan-ly-dich-vu/dich-vu"
            loading={tableLoading}
          />
        </div>
      </div>

      <AddServicePopup
        key={selectedService?._id || "new-service"}
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        data={selectedService}
        onSubmit={fetchListServices}
      />

      <ConfirmDeleteModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        loading={deleteLoading}
        title="Xóa dịch vụ"
        message="Bạn có chắc chắn muốn xóa dịch vụ này không?"
      />
    </div>
  );
};

export default ManageService;
